import { Box, Button, FormControl, IconButton, MenuItem, TextField, Theme } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { FontAttributes } from '@loyalty/shared-common/template-api-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfConfirmationDialog, colorPalette, useConfirmationDialog } from '@qualifio/ui-commons';
import { faPen } from '@fortawesome/pro-solid-svg-icons/faPen';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { makeStyles } from '@mui/styles';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomFontDialog, CustomFontDialogMode } from './CustomFontDialog';
import { FieldProps, LayoutFont } from './FieldProps';
import { useDeleteFont, useFonts } from '../../../hooks';

const useStyles = makeStyles(({ spacing }: Theme) => ({
  iconButton: {
    color: colorPalette.grey.main,
    marginLeft: spacing(0.5),
    '&:hover': {
      backgroundColor: 'inherit',
      color: colorPalette.turquoise.main,
    },
  },
  addCustomFontMenuItem: {
    backgroundColor: colorPalette.grey.light,
    '&:hover': {
      backgroundColor: colorPalette.grey.light,
    },
  },
  addFontIcon: {
    marginRight: spacing(1),
  },
}));

export function FontsSelect(props: FieldProps<LayoutFont>) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { name, type, endAdornment, onApply, size, options, ...rest } = props;

  const [customFontDialogMode, setCustomFontDialogMode] = useState<CustomFontDialogMode>('close');
  const [selectedFont, setSelectedFont] = useState<FontAttributes>();

  // Use React Query hooks
  const { data: fonts = [] } = useFonts();
  const { mutateAsync: deleteFont } = useDeleteFont();

  const { t } = useTranslation();
  const classes = useStyles();

  const { confirmationDialogState, setConfirmationDialogState } = useConfirmationDialog();

  const { control, setValue, watch } = useFormContext();

  const fieldValue = watch(name);

  const showDeleteFontDialog = (fontId: string) =>
    setConfirmationDialogState({
      ...confirmationDialogState,
      open: true,
      color: 'primary',
      title: t('editor.custom-font.confirmation-dialog.title'),
      content: t('editor.custom-font.confirmation-dialog.description'),

      confirmButtonColor: 'secondary',
      confirmButtonText: t('editor.custom-font.confirmation-dialog.delete-font'),

      cancelButtonText: t('generic.actions.cancel'),

      onConfirm: async () => {
        await deleteFont(fontId);
      },
    });

  const handleFontCreation = useCallback(
    (font: FontAttributes) => {
      setValue(name, font.name);
      onApply?.();
    },
    [setValue, name, onApply],
  );

  return (
    <>
      <Controller
        control={control}
        name={name}
        render={({ field: { ref, onChange, ...field } }) => (
          <FormControl fullWidth>
            <TextField
              select
              {...rest}
              {...field}
              onChange={event => {
                onChange(event);
                onApply?.();
              }}
              label=""
            >
              {[
                ...(options || []).map(option => (
                  <MenuItem value={option.value} key={option.value}>
                    <Box alignItems="center" display="flex" width="100%">
                      <Box flexGrow={1} display="flex" style={{ fontFamily: `${option.family}` }}>
                        {option.text}
                      </Box>
                      {option.id && (
                        <Box
                          sx={{
                            '.MuiSelect-select &': {
                              display: 'none',
                            },
                          }}
                        >
                          <IconButton
                            className={classes.iconButton}
                            size="small"
                            onClick={() => {
                              setCustomFontDialogMode('edit');
                              setSelectedFont(fonts.find(font => font.id === option.id));
                            }}
                          >
                            {option.id && <FontAwesomeIcon size="xs" icon={faPen} />}
                          </IconButton>
                          <IconButton
                            className={classes.iconButton}
                            size="small"
                            onClick={event => {
                              event.stopPropagation();
                              showDeleteFontDialog(option.id);
                            }}
                          >
                            <FontAwesomeIcon size="xs" icon={faTrash} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </MenuItem>
                )),
                <MenuItem
                  key="add-custom-font"
                  className={classes.addCustomFontMenuItem}
                  onClick={() => {
                    setCustomFontDialogMode('add');
                    setTimeout(() => setValue(field.name, fieldValue), 50);
                  }}
                >
                  <Button color="secondary" size="small" sx={{ borderRadius: '50px' }}>
                    <FontAwesomeIcon icon={faPlus} className={classes.addFontIcon} />
                    {t('editor.custom-fonts.fields.add-custom-font')}
                  </Button>
                </MenuItem>,
              ]}
            </TextField>
          </FormControl>
        )}
      />

      {(customFontDialogMode === 'add' || selectedFont) && (
        <CustomFontDialog
          mode={customFontDialogMode}
          onClose={() => {
            setCustomFontDialogMode('close');
            setSelectedFont(undefined);
          }}
          onAdd={handleFontCreation}
          font={selectedFont}
        />
      )}

      <QlfConfirmationDialog {...confirmationDialogState} />
    </>
  );
}
